import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { AiOutlineLoading } from "react-icons/ai";
import { useGenerateContentContext } from "../../../context/GenerateContentContext";
import { MdLightbulbOutline, MdContentCopy } from "react-icons/md";

const GenerateDescription = ({ setShowDescriptionModal, setDescription }) => {
  const [prompt, setPrompt] = useState("");
  const [aiContent, setAiContent] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const { generateContent } = useGenerateContentContext();

  const sendPrompt = async () => {
    if (!prompt || prompt === "") setIsError(true);
    setLoading(true);
    setIsError(false);
    setAiContent("");
    const response = await generateContent({ data: { message: prompt } });
    setLoading(false);
    if (response) setAiContent(response?.content || "");
  };

  const copyResult = async () => {
    if (!aiContent) return;
    try {
      await navigator.clipboard.writeText(aiContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { }
  };

  const quickPrompts = [
    "Write a concise travel intro for the region, highlighting culture and landscapes.",
    "Describe must-see attractions, best time to visit, and local etiquette.",
    "Create a friendly, SEO-friendly overview with 120–160 words.",
  ];

  const charCount = prompt ? String(prompt).length : 0;
  const resultCharCount = aiContent ? String(aiContent).length : 0;

  const btnBase =
    "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";
  const btnPrimary =
    "bg-blue-600 text-white border-blue-600 hover:bg-blue-500 focus-visible:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnNeutral =
    "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 disabled:opacity-60";

  return (
    <div className="fixed inset-0 z-[2147483647]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
      <div className="absolute inset-0 flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
        <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden my-auto">
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b bg-white/95 backdrop-blur">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold tracking-tight text-slate-900">
                Generate Description
              </span>
              <span className="hidden sm:inline text-[11px] text-slate-500">
                Craft clear, helpful copy for your region.
              </span>
            </div>
            <button
              onClick={() => setShowDescriptionModal(false)}
              className={`${btnBase} ${btnNeutral} h-9 w-9 p-0`}
              aria-label="Close"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>

          <div className="px-5 pt-4 pb-5 space-y-5">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
              <div className="flex items-start gap-2">
                <MdLightbulbOutline className="mt-[2px] h-5 w-5 text-amber-500" />
                <div className="flex-1">
                  <p className="text-[12px] text-slate-700">
                    Tip: Be specific about tone, length, and key points. Try a quick prompt:
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {quickPrompts.map((q, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setPrompt(q);
                          setIsError(false);
                        }}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-700 hover:bg-slate-100"
                      >
                        {q.length > 60 ? `${q.slice(0, 60)}…` : q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold text-slate-900">
                Enter your prompt
              </label>
              <textarea
                rows={7}
                value={prompt}
                onChange={(e) => {
                  setIsError(false);
                  setPrompt(e.target.value);
                }}
                placeholder="e.g., Write a concise travel overview for [Region], highlighting culture, landscapes, top attractions, and best time to visit. Keep it friendly and informative."
                className={`w-full rounded-xl border p-4 text-xs outline-none placeholder:text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isError ? "border-red-600" : "border-slate-300"
                  }`}
              />
              <div className="mt-1 flex items-center justify-between">
                <span className={`text-[11px] ${isError ? "text-red-600" : "text-slate-500"}`}>
                  {isError ? "Please add a prompt to get better results." : " "}
                </span>
                <span className="text-[11px] text-slate-400">{charCount} chars</span>
              </div>

              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setPrompt("")}
                  className={`${btnBase} ${btnNeutral}`}
                >
                  Clear
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={sendPrompt}
                  className={`${btnBase} ${btnPrimary}`}
                >
                  {loading ? (
                    <>
                      <AiOutlineLoading className="h-4 w-4 animate-spin" />
                      Generating…
                    </>
                  ) : aiContent === "" ? (
                    "Generate"
                  ) : (
                    "Re-Generate"
                  )}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <p className="text-xs font-semibold text-slate-900">Generated Result</p>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400">{resultCharCount} chars</span>
                  <button
                    type="button"
                    onClick={copyResult}
                    disabled={!aiContent}
                    className={`${btnBase} ${btnNeutral} h-8 px-3 py-1.5 text-[11px]`}
                    aria-label="Copy generated result"
                  >
                    <MdContentCopy className="h-4 w-4" />
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div className="px-4 py-4">
                <p className="whitespace-pre-line text-xs leading-relaxed text-slate-800">
                  {aiContent && aiContent !== "" ? aiContent : "—"}
                </p>
              </div>
            </div>

            <div className="pt-1">
              <button
                disabled={loading || isError || aiContent === ""}
                onClick={() => {
                  setDescription(aiContent);
                  setShowDescriptionModal(false);
                }}
                className={`${btnBase} ${btnPrimary} w-full justify-center uppercase tracking-wide`}
              >
                Insert Description
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateDescription;
